import {
  useDeleteReportTemplateMutation,
  useGetReportTemplatesQuery,
  useLazyGetReportTemplateDownloadUrlQuery,
} from '@consigli/hooks';
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@consigli/shadcn';
import { ReportTemplate } from '@consigli/types';
import { isRebuildEnvironment, saveFile } from '@consigli/utils';
import { useState } from 'react';
import { HiOutlineDownload, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';

import { errorToast, successToast } from '@/components/toast';

import { EditTemplateForm } from './edit-template-form';

export const TemplatesTable = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useGetReportTemplatesQuery({
    page,
    pageSize: 20,
    search: search || undefined,
    report_type: reportTypeFilter || undefined,
    project_type: projectTypeFilter || undefined,
    location: locationFilter || undefined,
  });

  const [deleteTemplate] = useDeleteReportTemplateMutation();
  const [fetchDownloadUrl] = useLazyGetReportTemplateDownloadUrlQuery();

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the template "${name}"?`)) {
      return;
    }

    try {
      await deleteTemplate({ id }).unwrap();
      successToast({ message: 'Template deleted successfully' });
      refetch();
    } catch {
      errorToast({ message: 'Failed to delete template' });
    }
  };

  const handleDownload = async (template: ReportTemplate) => {
    try {
      // Legacy Consigli storage serves report-template blobs publicly, so the
      // stored fileUrl downloads directly. The rebuilt AECOM estate keeps the
      // container private, so it needs a short-lived SAS URL from the backend.
      let fileUrl = template.fileUrl;
      if (isRebuildEnvironment()) {
        const { downloadUrl } = await fetchDownloadUrl({ id: template.id }).unwrap();
        fileUrl = downloadUrl;
      }

      saveFile(template.fileName, fileUrl);
    } catch (error) {
      console.error('Failed to download template:', error);
      errorToast({ message: 'Failed to download template' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle both paginated response format and direct array format
  const isArrayResponse = Array.isArray(data);
  const templates = isArrayResponse ? data : data?.results || [];
  const totalCount = isArrayResponse ? data.length : data?.count || 0;
  const hasNextPage = isArrayResponse ? false : data?.next !== null;
  const hasPreviousPage = isArrayResponse ? false : data?.previous !== null;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Input
          type="text"
          placeholder="Filter by report type..."
          value={reportTypeFilter}
          onChange={(e) => {
            setReportTypeFilter(e.target.value);
            setPage(1);
          }}
        />
        <Input
          type="text"
          placeholder="Filter by project type..."
          value={projectTypeFilter}
          onChange={(e) => {
            setProjectTypeFilter(e.target.value);
            setPage(1);
          }}
        />
        <Input
          type="text"
          placeholder="Filter by location..."
          value={locationFilter}
          onChange={(e) => {
            setLocationFilter(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Report Type</TableHead>
              <TableHead>Project Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || isFetching ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  Loading templates...
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                  No templates found. Upload your first template to get started.
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template: ReportTemplate) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.name}</TableCell>
                  <TableCell>{template.reportType}</TableCell>
                  <TableCell>{template.projectType}</TableCell>
                  <TableCell>{template.location}</TableCell>
                  <TableCell className="text-sm text-gray-600">{template.fileName}</TableCell>
                  <TableCell>{template.createdByUsername || 'Unknown'}</TableCell>
                  <TableCell>{formatDate(template.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingTemplateId(template.id)}
                        title="Edit template"
                      >
                        <HiOutlinePencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(template)}
                        title="Download template"
                      >
                        <HiOutlineDownload className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id, template.name)}
                        title="Delete template"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <HiOutlineTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {Math.min((page - 1) * 20 + 1, totalCount)} to {Math.min(page * 20, totalCount)}{' '}
            of {totalCount} templates
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={!hasPreviousPage || isLoading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Template Form */}
      {editingTemplateId && (
        <EditTemplateForm
          templateId={editingTemplateId}
          open={!!editingTemplateId}
          onOpenChange={(open) => {
            if (!open) {
              setEditingTemplateId(null);
            }
          }}
          onSuccess={() => {
            refetch();
            setEditingTemplateId(null);
          }}
        />
      )}
    </div>
  );
};
